import React, { FC, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import {
  setAudioInputDeviceId,
  setAudioOutputDeviceId,
} from "@store/avatarSlice";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMediaDeviceList } from "@hooks/useMediaDeviceList";
import { browserSupportsSetSinkId } from "@utils/media-output";
import {
  CONVERSATION_PACING_VALUES,
  ConversationPacing,
  VOICE_INPUT_MODE_VALUES,
  VoiceInputMode,
} from "@models/conversation-settings";
import { useCompanionConversation } from "@components/Companion/CompanionConversationContext";

const ConversationAudioOptionsDialog: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const audioInputDeviceId = useSelector(
    (s: RootState) => s.avatar.audioInputDeviceId,
  );
  const audioOutputDeviceId = useSelector(
    (s: RootState) => s.avatar.audioOutputDeviceId,
  );
  const { audioInputs, audioOutputs, refresh } = useMediaDeviceList();
  const outputRoutingSupported = useMemo(() => browserSupportsSetSinkId(), []);
  const {
    conversationPacing,
    setConversationPacing,
    voiceInputMode,
    setVoiceInputMode,
    stopPttCapture,
    vadBargeInEnabled,
    setVadBargeInEnabled,
  } = useCompanionConversation();

  useEffect(() => {
    if (open) {
      void refresh();
    }
  }, [open, refresh]);

  const handleRefresh = useCallback(() => {
    void refresh();
  }, [refresh]);

  const onPacingChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: ConversationPacing | null) => {
      if (value) {
        setConversationPacing(value);
      }
    },
    [setConversationPacing],
  );

  const onInputModeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: VoiceInputMode | null) => {
      if (value) {
        setVoiceInputMode(value);
        stopPttCapture();
      }
    },
    [setVoiceInputMode, stopPttCapture],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("companion.audioOptions.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="companion-audio-input-label">
              {t("companion.audioOptions.microphone")}
            </InputLabel>
            <Select
              labelId="companion-audio-input-label"
              label={t("companion.audioOptions.microphone")}
              value={audioInputDeviceId || ""}
              onChange={(e) =>
                dispatch(setAudioInputDeviceId(e.target.value as string))
              }
            >
              <MenuItem value="">
                {t("companion.audioOptions.defaultDevice")}
              </MenuItem>
              {audioInputs.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            size="small"
            disabled={!outputRoutingSupported}
          >
            <InputLabel id="companion-audio-output-label">
              {t("companion.audioOptions.speaker")}
            </InputLabel>
            <Select
              labelId="companion-audio-output-label"
              label={t("companion.audioOptions.speaker")}
              value={audioOutputDeviceId || ""}
              onChange={(e) =>
                dispatch(setAudioOutputDeviceId(e.target.value as string))
              }
            >
              <MenuItem value="">
                {t("companion.audioOptions.defaultDevice")}
              </MenuItem>
              {audioOutputs.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
            {!outputRoutingSupported ? (
              <FormHelperText>
                {t("companion.audioOptions.outputNotSupported")}
              </FormHelperText>
            ) : null}
          </FormControl>

          <Stack spacing={1}>
            <FormHelperText>{t("companionConversation.settings.inputMode")}</FormHelperText>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={voiceInputMode}
              onChange={onInputModeChange}
            >
              {VOICE_INPUT_MODE_VALUES.map((value) => (
                <ToggleButton key={value} value={value}>
                  {t(`companionConversation.inputMode.${value}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Stack spacing={0.75} sx={{ pt: 0.5 }}>
              {VOICE_INPUT_MODE_VALUES.map((value) => (
                <Typography
                  key={value}
                  variant="body2"
                  color="text.secondary"
                  component="p"
                  sx={{ m: 0 }}
                >
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {t(`companionConversation.inputMode.${value}`)}
                  </Typography>
                  {" — "}
                  {t(`companionConversation.inputModeDescription.${value}`)}
                </Typography>
              ))}
            </Stack>
            {voiceInputMode === "vad" ? (
              <>
                <Stack spacing={1} sx={{ pt: 0.5 }}>
                  <FormHelperText sx={{ m: 0 }}>
                    {t("companionConversation.settings.pacing")}
                  </FormHelperText>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={conversationPacing}
                    onChange={onPacingChange}
                  >
                    {CONVERSATION_PACING_VALUES.map((value) => (
                      <ToggleButton key={value} value={value}>
                        {t(`companionConversation.pacing.${value}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
                <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={vadBargeInEnabled}
                        onChange={(_, checked) => setVadBargeInEnabled(checked)}
                        size="small"
                      />
                    }
                    label={t("companionConversation.settings.vadBargeIn")}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                    {t("companionConversation.settings.vadBargeInHint")}
                  </Typography>
                </Stack>
              </>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleRefresh}>
          {t("companion.audioOptions.refreshDevices")}
        </Button>
        <Button variant="contained" onClick={onClose}>
          {t("companion.audioOptions.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationAudioOptionsDialog;
